import { html, css } from 'lit';
import { when } from 'lit-html/directives/when.js';
import { classMap } from 'lit-html/directives/class-map.js';
import { ref, createRef } from 'lit-html/directives/ref.js';
import { connect } from 'pwa-helpers';
import { VseApp } from '@components/vse-app/vse-app.js';

import { MobxReactionUpdate } from '@adobe/lit-mobx/lib/mixin.js';
import { ObservableProperties } from '@mixins/vst-observable-properties-mixin.js';

import { VideoUtils } from '@wasm/VideoUtils.js';
import { Provider } from '@mixins/vst-core-provider-mixin.js';
import { DialogMediator } from '@common/components/mixins/vst-core-dialog-mediator-mixin.js';
import { MeasurementType } from '@common/mobx-stores/Measurement.js';

import { getText } from '@utils/i18n.js';
import { isSmallScreen } from '@utils/screenSize.js';
import { nextTick } from '@utils/nextTick.js';
import { keyboardEvents } from '@utils/keyboardEvents.js';
import { sprintf } from '@libs/sprintf.js';
import {
  file,
  gear,
  overflow,
  view,
  videoAdvanceNext,
  videoAdvancePrev,
  videoPlay,
  videoSkipPrev,
} from '@icons';
import { helpIcon } from '@common/components/vst-ui-icon/svg/helpIcon.svg.js';

import { globalStyles } from '@styles/vst-style-global.css.js';
import { customPropertyStyles } from '@styles/vst-style-custom-properties.css.js';
import { breakpoint } from '@styles/mixins/breakpoints.css.js';
import { autorun } from 'mobx';

import { conditionalTemplate } from '@components/directives/conditionalTemplate.js';
import { vstLayoutStore } from '@stores/vst-layout.store.js';
import { vstAuthStore } from '@stores/vst-auth.store.js';
import { vstPresentationStore } from '@stores/vst-presentation.store.js';
import { captureEvent } from '@common/utils/analyticsHelpers.js';

import { isFeatureFlagEnabled } from '@services/featureflags/featureFlags.js';

import {
  createTourStep,
  createTourButtonStep,
  createFileMenuStep,
  createGraphOptionsStep,
  createOtherOptionsStep,
  createViewOptionsStep,
  createZoomStep,
  shouldSkipTour,
} from '@common/tour/tourHelpers.js';
import { AppTourProvider } from '@common/tour/appTourProvider.js';
import { store } from './redux/store.js';
import {
  resetSession,
  updateGraphsAutoLayout,
  updateIsSessionEmpty,
  enableReplay,
  disableReplay,
  setReplayTimestamp,
} from './redux/actions.js';

import { computeGraphLayout } from './graphAutoLayout.js';

import changelog from './changelog.json';

import '@components/vst-core-popover/vst-core-popover.js';
import '@components/vst-style-tooltip/vst-style-tooltip.js';
import '@components/vst-ui-content-layout-options/vst-ui-content-layout-options.js';
import '@components/vst-ui-dialog/vst-ui-dialog-manager.js';
import '@components/vst-ui-dialog/vst-ui-dialog.js';
import '@components/vst-ui-dropdown/vst-ui-dropdown.js';
import '@components/vst-ui-icon/vst-ui-icon.js';
import '@components/vst-ui-license-info/vst-ui-license-info.js';
import '@components/vst-ui-listbox/vst-ui-listbox.js';
import '@components/vst-ui-popover/vst-ui-popover.js';
import '@components/vst-ui-soft-alert/vst-ui-soft-alert.js';
import '@components/vst-ui-switch/vst-ui-switch.js';
import '@components/vst-ui-toast/vst-ui-toast.js';
import '@components/vst-ui-toolbar/vst-ui-toolbar.js';

import './va-welcome/va-welcome.js';
import './va-logo/va-logo.js';

const DAYS_TO_SHOW_WARNING = 30;
const DAYS_TO_SHOW_ERROR = 15;
const SAVEFILE_FILE_EXTENSIONS = ['vmbl', 'csv'];

const useServicesNext = isFeatureFlagEnabled('ff-enable-services-next');

export class VaApp extends connect(store)(
  AppTourProvider(Provider(DialogMediator(MobxReactionUpdate(ObservableProperties(VseApp))))),
) {
  #servicesReady = Promise.withResolvers();

  static get observableProperties() {
    return {
      colorMode: vstPresentationStore,
      daysRemaining: vstAuthStore,
      license: vstAuthStore,
      licenseExpiration: vstAuthStore,
      licenseExpired: vstAuthStore,
      licenseName: vstAuthStore,
    };
  }

  static get properties() {
    return {
      _moreOptionsMenuOpen: { state: true },
      _showAboutDialog: { state: true },
      _showAppPreferences: { state: true },
      _showFeatureFlagsDialog: { state: true },
      experimentName: { type: String },
      hasNativeFileSystem: { type: Boolean },
      inReplayMode: { type: Boolean },
      isSessionEmpty: { type: Boolean },
      layout: { type: Object },
      notify: { type: String },
      showFileMenu: { type: Boolean },
      showOverflowMenu: { type: Boolean },
      showSessionPreferencesDialog: { type: Boolean },
      showViewChangeMenu: { type: Boolean },
      showWelcomeDialog: { type: Boolean },
      showWhatsNew: { type: Boolean },
      supportsTwoGraphs: { type: Boolean },
      updateAvailable: { type: Boolean },
      videoLoaded: { type: Boolean },
    };
  }

  #replayRef = createRef();

  static get styles() {
    return [
      globalStyles,
      customPropertyStyles,
      css`
        :host {
          background-color: var(--vst-color-bg-secondary);
          height: 100%;
          display: flex;
          flex-direction: column;
          /* apply safe areas only if the environment specifies them */
          padding: env(safe-area-inset-top, 0) env(safe-area-inset-right, 0)
            env(safe-area-inset-bottom, 0) env(safe-area-inset-left, 0);

          --vst-color-brand-400: hsl(182, 100%, 16%);
          --vst-color-brand-300: hsl(182, 100%, 23%);
          --vst-color-brand-200: hsl(182, 70%, 35%);
          --vst-color-brand-100: hsl(182, 60%, 90%);
        }

        /* IOS Safari, Orientation Landscape Only */
        @supports (-webkit-touch-callout: none) {
          ${breakpoint(
            'landscape',
            css`
              :host {
                padding-block-start: 1.5rem;
              }
            `,
          )}
        }

        #welcome_dialog {
          --dialog-close-icon-color: #fff;
          --dialog-close-icon-hover-background: var(--vst-color-brand-dark);
          --dialog-border-block-start: none;
          --dialog-header-background: var(--vst-color-brand-300);
          --dialog-header-font-color: #fff;
        }
        [slot='version'][size='s'].caption {
          color: #ffffff;
        }

        .header {
          display: flex;
          flex-shrink: 0;
          width: 100%;
          height: 2.75rem;
          padding: 0 var(--vst-space-s);
          justify-content: center;
          align-items: center;
          background-color: var(--vst-color-brand);
          color: #fff;
          position: relative;
        }

        .header__logo {
          max-inline-size: 13.685rem;
          inline-size: 100%;
        }

        .header__version {
          white-space: nowrap;
          margin-inline-end: var(--vst-space-m);
          user-select: all;
          font-size: 0.5rem;
        }

        .banner {
          background: var(--vst-color-bg-tertiary);
          border: 1px solid var(--vst-color-bg-secondary);
          flex-shrink: 0;
          min-block-size: fit-content;
          padding: var(--vst-space-xs) var(--vst-space-s);
        }

        .footer {
          width: 100%;
        }

        #welcome-license-info.expired {
          background: var(--vst-color-danger);
          border: none;
        }
        #welcome-license-info.expired * {
          color: var(--vst-color-light-100);
        }

        @media only screen and (device-width: 375px) and (max-device-width: 812px) and (-webkit-device-pixel-ratio: 3),
          /* iphone XR */ screen and (device-width: 414px) and (device-height: 896px) and (-webkit-device-pixel-ratio: 2),
          /* iphone XS Max */ screen and (device-width: 414px) and (device-height: 896px) and (-webkit-device-pixel-ratio: 3),
          /* iphone XS Max Retina */ only screen and (-webkit-min-device-pixel-ratio: 3),
          only screen and (min--moz-device-pixel-ratio: 3),
          only screen and (-o-min-device-pixel-ratio: 3/1),
          only screen and (min-device-pixel-ratio: 3),
          only screen and (min-resolution: 458dpi),
          only screen and (min-resolution: 3dppx),
          /* Google Pixel 3 XL */ screen and (device-width: 360px) and (device-height: 740px) and (-webkit-min-device-pixel-ratio: 4),
          only screen and (min--moz-device-pixel-ratio: 4),
          only screen and (-o-min-device-pixel-ratio: 4/1),
          only screen and (min-device-pixel-ratio: 4),
          only screen and (min-resolution: 523dpi),
          only screen and (min-resolution: 4dppx) {
          @media (orientation: landscape) {
            :host {
              padding-block-start: env(safe-area-inset-top);
              padding-inline-start: env(safe-area-inset-left);
              padding-inline-end: env(safe-area-inset-right);
              padding-block-end: env(safe-area-inset-bottom);
            }
          }
        }

        .soft_alert_cookie_policy {
          --vst-ui-soft-alert-font-size: var(--vst-font-size-xs, 0.75rem);
        }

        .soft_alert_trial {
          z-index: calc(var(--vst-z-popover) - 1);
        }

        .toolbar {
          --toolbar-color-bg: #000;
          --color: var(--vst-color-light-100);
          --hover-color: #fff;
          --hover-bg: var(--vst-color-dark-200);
          touch-action: manipulation;
          position: relative;
        }

        .btn[variant='toolbar'] {
          --border-color: transparent;
        }

        .toolbar__filename {
          padding-inline-start: var(--vst-space-2xs);
          text-transform: initial;
        }

        #overflow_btn {
          position: relative;
        }

        .toolbar-overflow--whats-new__dot,
        .toolbar-overflow--update-available__dot {
          display: block;
          content: '';
          width: 0.5rem;
          height: 0.5rem;
          border-radius: 100%;
          position: absolute;
          margin: 0;
          top: -1px;
          right: -1px;
          background-color: var(--vst-color-danger);
        }

        .toolbar-overflow--whats-new__dot {
          background-color: var(--vst-color-success-ui); /* ideal contrast on black */
        }

        @media print {
          .toolbar {
            display: none;
          }
        }

        vst-ui-replay-controls {
          --replay-controls-background-color: var(--vst-color-dark-300);
          --replay-controls-border-color: var(--vst-color-dark-300);
          --replay-btn-icon-color: var(--vst-color-light-100);
          --replay-btn-color-hover: var(--vst-color-brand);
          --replay-btn-color-active: var(--vst-color-brand-dark);
        }
        #tour-btn svg {
          width: var(--vst-space-l);
          fill: var(--vst-color-light-100);
        }

        #ffmpeg_terms {
          justify-self: center;
          margin-block-start: var(--vst-space-2xs);
          max-inline-size: fit-content;
          text-align: center;
        }
      `,
    ];
  }

  static _disableReplay() {
    store.dispatch(disableReplay());
  }

  constructor() {
    super();
    this._showAboutDialog = false;
    this._showAppPreferences = false;
    this._showFeatureFlagsDialog = false;
    this._servicesReady = false;
    this._moreOptionsMenuOpen = false;
    this.$services = null;
    this.experimentName = getText('Untitled');
    this.showWhatsNew = false;
    this.showFileMenu = false;
    this.showViewChangeMenu = false;
    this.showOverflowMenu = false;
    this.showWelcomeDialog = false;
    this.showSessionPreferencesDialog = false;
    this.hasNativeFileSystem = false;
    this.updateAvailable = window._updateAvailable;

    this._tourButtonRef = createRef();

    window.addEventListener('update-available', () => {
      this.updateAvailable = true;
    });

    vstLayoutStore.initLayout({
      table: true,
      video: true,
    });

    this.fileMenuItems = { items: [] };
    this.viewMenuItems = {
      items: [],
    };
    this.overflowMenuItems = {
      items: [],
    };

    (async () => {
      try {
        this.videoUtils = new VideoUtils();
        await this.videoUtils.init();
        // eslint-disable-next-line no-console
        console.log('Video Utils Initialized'); // TODO: remove this once we've got this module working like we want it
      } catch (err) {
        console.error('Video Utils Initialization Failed');
        console.error(err);
      }
    })();

    window.matchMedia('(orientation: portrait) and (max-width: 30em)').addListener(() => {
      if (vstLayoutStore.openedPanes === 4 && vstLayoutStore.notes)
        vstLayoutStore.updateLayout({ notes: false });
    });

    this.addEventListener('va-graph-size-support', ({ detail: { support } }) => {
      this.supportsTwoGraphs = support;
    });
  }

  /**
   * @returns {VseAppInfo} information about the app that we can refer to when
   * saving and opening files et al.
   * @override
   */
  // eslint-disable-next-line class-methods-use-this
  get appInfo() {
    return {
      appName: 'Vernier Video Analysis',
      fileExtension: '.vmbl',
    };
  }

  async firstUpdated() {
    this.classList = this.colorMode;

    await this.#servicesReady.promise;

    this.hasNativeFileSystem = 'showOpenFilePicker' in window;
    this._saveAs = this.hasNativeFileSystem ? this.$services.fileIO.showSaveDialog : this.saveFile;

    this.fileMenuItems = {
      items: [
        {
          id: 'new',
          title: getText('New Experiment'),
          selectAction: () => this.onCreateExperiment(),
        },
      ],
    };

    this.fileMenuItems.items.push({
      id: 'open',
      title: getText('Open'),
    });

    if (this.hasNativeFileSystem) {
      this.fileMenuItems.items.push({
        id: 'save',
        title: getText('Save'),
      });
    }

    this.fileMenuItems.items.push({
      id: 'save_as',
      title: getText('Save As...'),
    });

    this.fileMenuItems.items.push({
      id: 'export',
      title: getText('Export...'),
    });

    this.overflowMenuItems = {
      items: [
        {
          id: 'about',
          title: getText('About'),
        },
        {
          id: 'app_preferences',
          title: getText('App Preferences'),
        },
        {
          id: 'session_preferences',
          title: getText('Session Preferences'),
        },
        {
          id: 'user_manual',
          title: getText('User Manual'),
        },
        {
          id: 'distribute_app',
          title: getText(`Distribute App`),
        },
        {
          id: 'whats_new',
          title: getText(`What's New`),
          className: this.notifyWhatsNew ? 'show-green-dot' : '',
        },
      ],
    };
    if (this.updateAvailable) this._triggerUpdateNotification();
    const { fileIO, popoverManager } = this.$services;

    await this.updateComplete;

    this._mainContentEl = this.shadowRoot.querySelector('#main_content');
    this._overflowMenuPopoverEl = this.shadowRoot.querySelector('#overflow_popover');
    const popoverWrapper = this.shadowRoot.querySelector('#popover_wrapper');
    popoverManager.attachWrapper(popoverWrapper);

    const toastWrapper = this.shadowRoot.querySelector('#toast_wrapper');
    this.$services.toast.attachWrapper(toastWrapper);
    fileIO.onSave(filename => {
      this.experimentName = filename;
    });

    await this.#servicesReady.promise;
    await this.initApp();
    if (!this.$services.urlHandler.hasFile) this.showWelcomeDialog = true;

    window.addEventListener('resize', this._resizeViewport.bind(this));
  }

  updated(changedProperties) {
    changedProperties.forEach((oldValue, propName) => {
      switch (propName) {
        case 'updateAvailable':
          this.notify = this.updateAvailable ? 'update-available' : '';
          this._triggerUpdateNotification();
          break;
        case 'colorMode':
          this.classList = this.colorMode;
          break;
        default:
      }
    });
  }

  async setServices(services) {
    this.$services = services;
    this.provideServices(this.$services);

    this.$services.appServicesInitialized.resolve(true);
    this.#servicesReady.resolve(true);
  }

  startAppTour(userInitiated = false) {
    if (!isFeatureFlagEnabled('ff-app-tour') || shouldSkipTour(userInitiated)) return;
    this.readyAppForTour();

    const steps = [
      createTourButtonStep({
        element: this._tourButtonRef.value,
        html: () =>
          html`<p>
            ${getText(
              'This short tour highlights key tools and features to help you get started with Video Analysis. You can return to the tour anytime by clicking the',
              'general',
              'App tour. Immediately followed by the text, "help icon in the top bar"',
            )}
            <span aria-hidden="true">${helpIcon}</span>
            <span visually-hidden>${getText('App Tour')}</span>
            ${getText(
              'icon in the top bar.',
              'general',
              'App tour. Immediately preceded by text, "This short tour highlights key tools and features to help you get started with Graphical Analysis. You can return to the tour anytime by clicking the"',
            )}
          </p>`,
      }),
      createFileMenuStep(this.shadowRoot.querySelector('#new_btn')),
      createTourStep({
        element: this.shadowRoot
          .querySelector('va-main-content')
          .shadowRoot.querySelector('va-video')
          .shadowRoot.querySelector('.video__import'),
        title: getText('Import a Video', 'general', 'va app tour'),
        html: () =>
          html`<p>
            ${getText(
              'Import one of your own videos or open a sample video.',
              'general',
              'App tour instructions',
            )}
          </p>`,
        side: 'top',
        align: 'start',
      }),
      createTourStep({
        title: getText('Add Points', 'general', 'va app tour'),
        side: 'right',
        align: 'start',
        element: this.shadowRoot
          .querySelector('va-main-content')
          .shadowRoot.querySelector('va-video')
          .shadowRoot.querySelector('#marking_tools_btn_marking'),
        html: () =>
          html`<p>
            <b>
              ${getText(
                'Add ',
                'general',
                'App tour instructions fragment. Full text: Add points manually or use the automatic Track feature to define an object’s motion on the frames of the video. Use the Vectors controls to choose which vectors to display, and define their relative scale and frequency.',
              )}
            </b>
            ${getText(
              'points manually or use the automatic ',
              'general',
              'App tour instructions fragment. Full text: Add points manually or use the automatic Track feature to define an object’s motion on the frames of the video. Use the Vectors controls to choose which vectors to display, and define their relative scale and frequency.',
            )}
            <b>
              ${getText(
                'Track ',
                'general',
                'App tour instructions fragment. Full text: Add points manually or use the automatic Track feature to define an object’s motion on the frames of the video. Use the Vectors controls to choose which vectors to display, and define their relative scale and frequency.',
              )}
            </b>
            ${getText(
              'feature to define an object’s motion on the frames of the video. Use the ',
              'general',
              'App tour instructions fragment. Full text: Add points manually or use the automatic Track feature to define an object’s motion on the frames of the video. Use the Vectors controls to choose which vectors to display, and define their relative scale and frequency.',
            )}
            <b>
              ${getText(
                'Vectors ',
                'general',
                'App tour instructions fragment. Full text: Add points manually or use the automatic Track feature to define an object’s motion on the frames of the video. Use the Vectors controls to choose which vectors to display, and define their relative scale and frequency.',
              )}
            </b>
            ${getText(
              'controls to choose which vectors to display, and define their relative scale and frequency.',
              'general',
              'App tour instructions fragment. Full text: Add points manually or use the automatic Track feature to define an object’s motion on the frames of the video. Use the Vectors controls to choose which vectors to display, and define their relative scale and frequency.',
            )}
          </p>`,
      }),
      createTourStep({
        title: getText('Edit', 'general', 'va app tour'),
        side: 'right',
        align: 'start',
        element: this.shadowRoot
          .querySelector('va-main-content')
          .shadowRoot.querySelector('va-video')
          .shadowRoot.querySelector('#marking_tools_btn_edit'),
        html: () =>
          html`<p>
            ${getText('Move or delete points from the video.', 'general', 'App tour instructions')}
          </p>`,
      }),
      createTourStep({
        title: getText('System Tools', 'general', 'va app tour'),
        side: 'right',
        align: 'start',
        element: this.shadowRoot
          .querySelector('va-main-content')
          .shadowRoot.querySelector('va-video')
          .shadowRoot.querySelector('#marking_tools_btn_system'),
        html: () =>
          html`<p>
            ${getText(
              'Set the ',
              'general',
              'App tour instructions fragment. Full text: Set the Scale and Origin for the video and switch between Cartesian and polar coordinates. Make measurements of the elements in the video with the Distance and Angle tools',
            )}
            <b>
              ${getText(
                'Scale ',
                'general',
                'App tour instructions fragment. Full text: Set the Scale and Origin for the video and switch between Cartesian and polar coordinates. Make measurements of the elements in the video with the Distance and Angle tools',
              )}
            </b>
            ${getText(
              'and ',
              'general',
              'App tour instructions fragment. Full text: Set the Scale and Origin for the video and switch between Cartesian and polar coordinates. Make measurements of the elements in the video with the Distance and Angle tools',
            )}
            <b>
              ${getText(
                'Origin ',
                'general',
                'App tour instructions fragment. Full text: Set the Scale and Origin for the video and switch between Cartesian and polar coordinates. Make measurements of the elements in the video with the Distance and Angle tools',
              )}
            </b>
            ${getText(
              'for the video and switch between Cartesian and polar coordinates. Make measurements of the elements in the video with the ',
              'general',
              'App tour instructions fragment. Full text: Set the Scale and Origin for the video and switch between Cartesian and polar coordinates. Make measurements of the elements in the video with the Distance and Angle tools',
            )}
            <b>
              ${getText(
                'Distance',
                'general',
                'App tour instructions fragment. Full text: Set the Scale and Origin for the video and switch between Cartesian and polar coordinates. Make measurements of the elements in the video with the Distance and Angle tools',
              )}
            </b>
            ${getText(
              'and ',
              'general',
              'App tour instructions fragment. Full text: Set the Scale and Origin for the video and switch between Cartesian and polar coordinates. Make measurements of the elements in the video with the Distance and Angle tools',
            )}
            <b>
              ${getText(
                'Angle ',
                'general',
                'App tour instructions fragment. Full text: Set the Scale and Origin for the video and switch between Cartesian and polar coordinates. Make measurements of the elements in the video with the Distance and Angle tools',
              )}
            </b>
            ${getText(
              'tools.',
              'general',
              'App tour instructions fragment. Full text: Set the Scale and Origin for the video and switch between Cartesian and polar coordinates. Make measurements of the elements in the video with the Distance and Angle tools',
            )}
          </p>`,
      }),
      createTourStep({
        title: getText('Trails', 'general', 'va app tour'),
        side: 'right',
        align: 'start',
        element: this.shadowRoot
          .querySelector('va-main-content')
          .shadowRoot.querySelector('va-video')
          .shadowRoot.querySelector('#marking_tools_btn_show_trails'),
        html: () =>
          html`<p>
            ${getText(
              'Toggle the display of data points on the video on and off.',
              'general',
              'App tour instructions',
            )}
          </p>`,
      }),
      createTourStep({
        title: getText('Objects', 'general', 'va app tour'),
        side: 'right',
        align: 'start',
        element: this.shadowRoot
          .querySelector('va-main-content')
          .shadowRoot.querySelector('va-video')
          .shadowRoot.querySelector('#marking_tools_btn_list_traces'),
        html: () =>
          html`<p>
            ${getText(
              'Add new objects to the video so that you can track the motion of multiple objects and optionally show their computed center of mass.',
              'general',
              'App tour instructions',
            )}
          </p>`,
      }),
      createViewOptionsStep({
        element: this.shadowRoot.querySelector('#view_change_btn'),
        html: () =>
          html`<p>
            ${getText(
              'Customize your screen layout. Show or hide the video, graphs, data table, and notes.',
            )}
          </p>`,
      }),
      createOtherOptionsStep(this.shadowRoot.querySelector('#overflow_btn')),
      createTourStep({
        title: getText('x and y axes'),
        side: 'right',
        align: 'center',
        html: () =>
          html`<p>${getText('Choose which data appear on your graph’s x- and y-axes.')}</p>`,
        element: this.shadowRoot
          .querySelector('va-main-content')
          .shadowRoot.querySelector('va-graph')
          .shadowRoot.querySelector('vst-core-graph')
          .shadowRoot.querySelector('#left_axis_label'),
      }),
      createGraphOptionsStep(
        this.shadowRoot
          .querySelector('va-main-content')
          .shadowRoot.querySelector('va-graph')
          .shadowRoot.querySelector('vst-core-graph')
          .shadowRoot.querySelector('#graph_tools_btn'),
      ),
      createZoomStep(
        this.shadowRoot
          .querySelector('va-main-content')
          .shadowRoot.querySelector('va-graph')
          .shadowRoot.querySelector('vst-core-graph')
          .shadowRoot.querySelector('#autoscale_btn'),
      ),
      createTourStep({
        title: getText('Playback Controls', 'general', 'va app tour'),
        side: 'top',
        align: 'start',
        element: this.shadowRoot
          .querySelector('va-main-content')
          .shadowRoot.querySelector('va-video')
          .shadowRoot.querySelector('#playback_wrapper'),
        html: () =>
          html`<p>
              ${getText(
                'Use the controls to ',
                'general',
                'App tour instructions fragment. Full text: Use the controls to Play or to Go to the beginning of the video, and the video scrubbing bar to move to a specific frame.',
              )}
              <b>
                ${getText(
                  'Play ',
                  'general',
                  'App tour instructions fragment. Full text: Use the controls to Play or to Go to the beginning of the video, and the video scrubbing bar to move to a specific frame.',
                )}
              </b>
              <vst-ui-icon aria-hidden="true" .icon="${videoPlay}"></vst-ui-icon>
              ${getText(
                ' or to ',
                'general',
                'App tour instructions fragment. Full text: Use the controls to Play or to Go to the beginning of the video, and the video scrubbing bar to move to a specific frame.',
              )}
              <b>
                ${getText(
                  'Go to the beginning of the video ',
                  'general',
                  'App tour instructions fragment. Full text: Use the controls to Play or to Go to the beginning of the video, and the video scrubbing bar to move to a specific frame.',
                )}
              </b>
              <vst-ui-icon aria-hidden="true" .icon="${videoSkipPrev}"></vst-ui-icon>
              ${getText(
                ', and the video scrubbing bar to move to a specific frame. ',
                'general',
                'App tour instructions fragment. Full text: Use the controls to Play or to Go to the beginning of the video, and the video scrubbing bar to move to a specific frame.',
              )}
              ${getText(
                'Move the Range Indicators, ',
                'general',
                'App tour instructions fragment. Full text: Move the Range Indicators at either end of the video scrubbing bar until only the relevant portion of the video plays.',
              )}
              <span aria-hidden="true">▲</span>
              ${getText(
                ', at either end of the video scrubbing bar until only the relevant portion of the video plays.',
                'general',
                'App tour instructions fragment. Full text: Move the Range Indicators at either end of the video scrubbing bar until only the relevant portion of the video plays.',
              )}
            </p>
            <p>
              ${getText(
                'Use the ',
                'general',
                'App tour instructions fragment. Full text: Use the Step Forward and Step Back to advance the video to the desired frame.',
              )}
              <b>
                ${getText(
                  'Step Forward ',
                  'general',
                  'App tour instructions fragment. Full text: Use the Step Forward and Step Back to advance the video to the desired frame.',
                )}
              </b>
              <vst-ui-icon aria-hidden="true" .icon="${videoAdvanceNext}"></vst-ui-icon>
              ${getText(
                ' and ',
                'general',
                'App tour instructions fragment. Full text: Use the Step Forward and Step Back to advance the video to the desired frame.',
              )}
              <b>
                ${getText(
                  'Step Back ',
                  'general',
                  'App tour instructions fragment. Full text: Use the Step Forward and Step Back to advance the video to the desired frame.',
                )}
              </b>
              <vst-ui-icon aria-hidden="true" .icon="${videoAdvancePrev}"></vst-ui-icon>
              ${getText(
                ' to advance the video to the desired frame.',
                'general',
                'App tour instructions fragment. Full text: Use the Step Forward and Step Back to advance the video to the desired frame.',
              )}
            </p>
            <p>
              ${getText(
                'Use the ',
                'general',
                'App tour instructions fragment. Full text: Use the Advance Video Options to change the number of frames to advance the video with each marked point, and change the video frame rate.',
              )}
              <b>
                ${getText(
                  'Advance Video Options ',
                  'general',
                  'App tour instructions fragment. Full text: Use the Advance Video Options to change the number of frames to advance the video with each marked point, and change the video frame rate.',
                )}
              </b>
              <vst-ui-icon aria-hidden="true" .icon="${gear}"></vst-ui-icon>
              ${getText(
                ' to change the number of frames to advance the video with each marked point, and change the video frame rate.',
                'general',
                'App tour instructions fragment. Full text: Use the Advance Video Options to change the number of frames to advance the video with each marked point, and change the video frame rate.',
              )}
            </p>`,
      }),
    ];
    super.startAppTour({ steps, userInitiated, tourButton: this._tourButtonRef.value });
  }

  _triggerUpdateNotification() {
    if (this.overflowMenuItems.items.some(item => item.id === 'check_for_updates')) return;
    this.overflowMenuItems.items.push({
      id: 'check_for_updates',
      title: getText('Update App'),
      className: 'show-red-dot',
    });
    const overflowList = this.shadowRoot.querySelector('#overflow-list');
    if (overflowList) overflowList.requestUpdate();
  }

  /**
   * Initializes application business logic. Called at `firstUpdated()`
   * @override
   */
  async initApp() {
    const { dataWorld, urlHandler, toast, nativeModules } = this.$services;

    if (urlHandler.checkOpeningUrl) {
      urlHandler.on('startup-url-error', ({ status, fileType, location, error }) => {
        toast.makeToast(
          `${status}: ${error ?? `Failed to fetch ${fileType} from location: ${location}`}`,
          { duration: 5000 },
        );
        this.showWelcomeDialog = true;
      });
      urlHandler.on('startup-url-experiment', ({ fileData }) => {
        this.onOpenFile({ detail: { fileData } });
      });
      urlHandler.on('startup-url-video', ({ videoData }) => {
        this.onImportVideo({
          detail: videoData,
        });
      });
      await urlHandler.checkOpeningUrl(nativeModules.validateJWT);
    }

    await vstAuthStore.verifyLicense();
    if (!this.license) window.location.replace('/auth/');

    await super.initApp();

    await Promise.allSettled([import('./va-main-content.js')]);

    this.registerDialogTemplate([
      'vst-core-column-options',
      'vst-ui-message-box',
      'vst-ui-custom-expression-info',
      'vst-core-export',
      'vst-core-fit-options',
    ]);

    this._setupAutoLayout();
    this._setupCustomCurveFits();

    this.resetStoreSession = () => {
      const graphIds = ['graph_1', 'graph_2'];
      store.dispatch(resetSession(graphIds));
    };

    dataWorld.on('session-ended', () => {
      this.experimentName = getText('Untitled');
      this._currentFileHandle = null;
      store.dispatch(disableReplay());
      this.resetStoreSession();
    });

    dataWorld.on('column-values-changed', () => {
      store.dispatch(updateIsSessionEmpty(false));
    });

    this.resetStoreSession();

    dataWorld.beginChurn();
    await this.$services.experiment.startNewSession('DataCollection', {});

    this.dispatchEvent(new CustomEvent('app-initialized'));
  }

  // /////////////////////////
  _setupCustomCurveFits() {
    const { dataWorld, dataAnalysis } = this.$services;

    this.eventBinder.on(dataWorld, 'session-ended', async () => {
      await dataAnalysis.curveFitStore.removeAllFits();
    });
  }

  _setupAutoLayout() {
    const { dataWorld } = this.$services;

    const initialLayout = {
      video: true,
      graph_1: true,
      graph_2: false,
      table: window.matchMedia('(min-width: 30em)').matches,
      notes: false,
    };

    const updateGraphsAndLayout = (graphsLayout, appAutoLayout) => {
      store.dispatch(updateGraphsAutoLayout(graphsLayout));
      vstLayoutStore.updateLayout(appAutoLayout);
    };

    this.eventBinder.on(dataWorld, 'session-started', async config => {
      if (!config.imported) {
        const graphsLayout = computeGraphLayout(dataWorld);
        updateGraphsAndLayout(graphsLayout, initialLayout);
      } else {
        // Fetch layout properties from UDM.
        const layoutFlags = await dataWorld.getLayoutProperties();
        if (layoutFlags) vstLayoutStore.updateLayout(layoutFlags);
      }

      // Set up an autorun that responds to changes to the layout store and
      // saves the values to UDM.
      this._stopLayoutAutorun = autorun(() => {
        dataWorld.setLayoutProperties({
          graph_1: vstLayoutStore.graph_1,
          graph_2: vstLayoutStore.graph_2,
          graph_3: vstLayoutStore.graph_3,
          table: vstLayoutStore.table,
          meter: vstLayoutStore.meter,
          video: vstLayoutStore.video,
          notes: vstLayoutStore.notes,
          configurator: vstLayoutStore.configurator,
        });
      });
    });

    this.eventBinder.bindListeners({
      source: keyboardEvents,
      target: this,
      eventMap: {
        'open-file': 'onImportOrOpenFile',
      },
    });
  }

  async _toggleAppPreferencesDialog(value) {
    await import('@components/vst-ui-app-preferences/vst-ui-app-preferences.js');
    this._showAppPreferences = value;
  }

  _toggleFileMenu(state) {
    // this way if it fires on mouseup and click, it fires the same direction (open/close)
    this.showFileMenu = state;
  }

  async _handleFileMenuItemClicked({ detail: id }) {
    if (id === 'open') {
      this.onImportOrOpenFile();
    } else if (id === 'save') {
      this.onSaveFile({ saveAs: !this._currentFileHandle });
    } else if (id === 'save_as') {
      this.onSaveFile({ saveAs: true });
    } else if (id === 'new') {
      this.onCreateExperiment();
    } else if (id === 'export') {
      this._export();
    }

    this._toggleFileMenu();
  }

  _toggleViewChangeMenu() {
    this.showViewChangeMenu = !this.showViewChangeMenu;
  }

  static _handleViewMenuItemChanged({
    detail: { layoutOptions, graphOptions, changedLayoutOptionId },
  }) {
    // TODO: there is some tech debt here that needs to be addressed, if a popover isn't shown its still
    // in the DOM and the components are still being updated, so you can have unintended side-effects
    // like this method being called unexpectedly
    if (!this.showViewChangeMenu) return; // if the menu is closed the view was updated via a file hydration

    let checkedlayoutIds = layoutOptions.filter(option => option.checked).map(option => option.id);

    if (
      matchMedia('(orientation: portrait) and (max-width: 30em)').matches &&
      checkedlayoutIds.length > 2
    ) {
      // limit number of enabled layout switches to 2 on small screens
      const numAllowedSwitches = 2;
      const priorityOrderLayoutIds = [
        changedLayoutOptionId,
        ...['video', 'graphs', 'table', 'notes'].filter(id => id !== changedLayoutOptionId),
      ];
      checkedlayoutIds = priorityOrderLayoutIds
        .filter(id => checkedlayoutIds.includes(id))
        .slice(0, numAllowedSwitches);
    }

    const newLayout = {
      graph_1: false,
      graph_2: false,
      table: false,
      video: false,
      notes: false,
    };

    checkedlayoutIds.forEach(id => {
      if (id === 'graphs') {
        newLayout.graph_1 = true;
        if (graphOptions.length) {
          newLayout.graph_2 = graphOptions[1].selected;
        }
      } else if (id === 'video' || id === 'table' || id === 'notes') {
        newLayout[id] = true;
      }
    });

    if (this.joinedGraphs === undefined && newLayout.graph_2 === true) this.joinedGraphs = true;

    vstLayoutStore.updateLayout(newLayout);
    this.$services.experiment.setVideoVisibility(newLayout.video);
  }

  _toggleOverflowMenu() {
    this._moreOptionsMenuOpen = true;
  }

  /**
   * Handles the open file event from va-welcome.
   *
   * Does not handle the case where the user tries to open a video file because
   * this event is only emitted if the file is not a video file.
   *
   * @param {CustomEvent} event - The event object.
   */
  #handleWelcomeOpenFile(event) {
    const { file } = event.detail;

    if (VaApp.#isValidSavefile(file)) {
      this.onOpenFile({ detail: { fileData: { file, filepath: file.name, name: file.name } } });
    } else {
      this.onImportVideo({ detail: { file } });
    }
  }

  async _handleOverflowItemClicked({ detail: id }) {
    if (id === 'about') {
      await import('@components/vst-ui-about/vst-ui-about.js');
      this._showAboutDialog = true;
    } else if (id === 'session_preferences') {
      await import('@components/vst-core-session-preferences/vst-core-session-preferences.js');
      this.showSessionPreferencesDialog = true;
    } else if (id === 'user_manual') {
      window.open(
        'http://www2.vernier.com/manuals/video-analysis-manual.pdf',
        '_blank',
        'noopener',
      );
    } else if (id === 'distribute_app') {
      await import('@components/vst-ui-distribute-app/vst-ui-distribute-app.js');
      this.$services.popoverManager.presentDialog('vst-ui-distribute-app', {
        title: getText('Distribute Vernier Video Analysis'),
      });
    } else if (id === 'check_for_updates') {
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        reg.waiting.postMessage({ type: 'SKIP_WAITING' });
      } catch (error) {
        // likely error, reg.waiting doesn't exist and we somehow still triggered updateAvailable
        // so we will reload the page, since that meets the expectations of the user and should clear the state.
        window.location.reload();
      }
    } else if (id === 'whats_new') {
      await import('@components/vst-ui-changelog/vst-ui-changelog.js');
      this.$services.popoverManager.presentDialog('vst-ui-changelog', {
        title: getText(`What's New`),
        properties: {
          releases: changelog,
        },
      });
      if (this.notifyWhatsNew) {
        this.notifyWhatsNew = false;
        this.notify = '';
      }
    } else if (id === 'app_preferences') {
      this._toggleAppPreferencesDialog(true);
    }

    this._overflowMenuPopoverEl.hide();
  }

  static #isValidSavefile(file) {
    return SAVEFILE_FILE_EXTENSIONS.some(extension =>
      file.name.toLowerCase().endsWith(`.${extension}`),
    );
  }

  async #onOpenFeatureFlagsDialog() {
    await import('@components/vst-core-feature-flags/vst-core-feature-flags.js');
    this._showFeatureFlagsDialog = true;
  }

  /**
   * File save implementation specific to VA app.
   * @param {object} [options] for fileIO save file. These are not fully
   * documented (TODO)
   * @override
   */
  async onSaveFile(options = {}) {
    try {
      options.fileHandle = this._currentFileHandle;
      const fileHandle = await this.$services.fileIO.saveFile(options); // could return false if user cancelled save

      if (!fileHandle) return; // user aborted request

      this._currentFileHandle = fileHandle;
      this.$services.toast.makeToast(sprintf(getText('%s saved'), fileHandle.name), {
        duration: 5000,
      });
    } catch (err) {
      console.error(err);
    }
  }

  async onImportOrOpenFile() {
    try {
      const mimeTypes =
        'showSaveFilePicker' in window || navigator.maxTouchPoints === 0 ? ['video/*'] : ['*'];
      const { file, fileHandle } = await this.$services.fileIO.showOpenDialog([
        {
          extensions: ['vmbl'],
          mimeTypes,
          name: 'Vernier Video Analysis',
        },
      ]);

      if (VaApp.#isValidSavefile(file)) {
        this._currentFileHandle = fileHandle; // TODO clear out on file new
        this.onOpenFile({ detail: { fileData: { file, filepath: file.name, name: file.name } } });
      } else {
        this.onImportVideo({ detail: { file } });
      }
    } catch (err) {
      console.warn(err);
    }
  }

  /**
   * Render the video pane as an image
   *
   * @param {object} [options]
   * @param {boolean} [options.showMeasurements]
   * @param {boolean} [options.showOrigin]
   * @param {boolean} [options.showScale]
   * @return {Promise.<string>} Promise that resolves with a data URI of the image
   */
  async _exportVideo({ showMeasurements = true, showOrigin = true, showScale = true } = {}) {
    const exportCanvas = document.createElement('canvas');
    const exportContext = exportCanvas.getContext('2d');
    const vaVideo = this._mainContentEl.vaVideoEl;
    vaVideo.pausePointCanvas = true;
    await nextTick();
    const baseCanvas = vaVideo.videoEl;
    const pointCanvas = vaVideo.pointCanvasEl.canvas;
    const [height, width] = vaVideo.anyVectorEnabled
      ? [pointCanvas.height / 2, pointCanvas.width / 2]
      : [baseCanvas.height, baseCanvas.width];
    const baseOffsetX = (width - baseCanvas.width) / 2;
    const baseOffsetY = (height - baseCanvas.height) / 2;
    const overlayOffsetX = (width - pointCanvas.width / 2) / 2;
    const overlayOffsetY = (height - pointCanvas.height / 2) / 2;
    const distanceMeasurementCanvas = await vaVideo.shadowRoot
      .querySelector('va-distance-tool')
      ?.export?.();
    const angleMeasurementCanvas = await vaVideo.shadowRoot
      .querySelector('va-angle-tool')
      ?.export?.();
    const originCanvas = await vaVideo.shadowRoot.querySelector('va-origin-control')?.export?.();
    const scaleCanvas = await vaVideo.shadowRoot.querySelector('va-scale-control')?.export?.();
    exportCanvas.height = height;
    exportCanvas.width = width;
    exportContext.drawImage(
      baseCanvas,
      baseOffsetX,
      baseOffsetY,
      baseCanvas.width,
      baseCanvas.height,
    );

    const { dataWorld } = this.$services;
    const hasMeasurements =
      dataWorld.getMeasurementToolsByType(MeasurementType.DISTANCE).length > 0 ||
      dataWorld.getMeasurementToolsByType(MeasurementType.ANGLE).length > 0;

    // Check if point canvas has height and width. When video is disabled the height/width will be 0.
    // Do not draw image when video is disabled.
    if (pointCanvas.height > 0 && pointCanvas.width > 0) {
      exportContext.drawImage(
        pointCanvas,
        overlayOffsetX,
        overlayOffsetY,
        pointCanvas.width / 2,
        pointCanvas.height / 2,
      );
      if (distanceMeasurementCanvas && hasMeasurements && showMeasurements) {
        exportContext.drawImage(
          distanceMeasurementCanvas,
          baseOffsetX,
          baseOffsetY,
          baseCanvas.width,
          baseCanvas.height,
        );
      }
      if (angleMeasurementCanvas && hasMeasurements && showMeasurements) {
        exportContext.drawImage(
          angleMeasurementCanvas,
          baseOffsetX,
          baseOffsetY,
          baseCanvas.width,
          baseCanvas.height,
        );
      }
      if (originCanvas && vaVideo.originSetByUser && showOrigin) {
        exportContext.drawImage(
          originCanvas,
          baseOffsetX,
          baseOffsetY,
          baseCanvas.width,
          baseCanvas.height,
        );
      }
      if (scaleCanvas && vaVideo.scaleSetByUser && showScale) {
        exportContext.drawImage(
          scaleCanvas,
          baseOffsetX,
          baseOffsetY,
          baseCanvas.width,
          baseCanvas.height,
        );
      }
    }

    vaVideo.pausePointCanvas = false;
    return height !== 0 && width !== 0
      ? exportCanvas.toDataURL()
      : // Transparent 1x1 PNG
        'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAACklEQVR4nGMAAQAABQABDQottAAAAABJRU5ErkJggg==';
  }

  async _export() {
    return this._openExport(await this._exportVideo());
  }

  async _openExport(videoCapture) {
    const { dataWorld } = this.$services;
    const exportTabs = [
      ...Array.from(
        this._mainContentEl.shadowRoot.querySelectorAll(
          'va-graph:first-child, va-graph:not([hidden])',
        ),
      ).map((graphEl, index) => {
        return {
          name: sprintf(getText('Graph %s'), index + 1),
          type: 'graph',
          output: 'image',
          graphEl,
        };
      }),
      {
        name: getText('CSV'),
        type: 'csv',
        output: 'file',
        extension: 'csv',
        tooltip: getText('Comma Separated Values'),
      },
    ];
    if (vstLayoutStore.video) {
      exportTabs.push({
        name: getText('Video'),
        type: 'frame',
        output: 'image',
        image: videoCapture,
        alt: getText(
          'This is a still from the video you are exporting and it includes the annotations that you added.',
        ),
        aspectRatio:
          this._mainContentEl.vaVideoEl.videoEl.videoWidth /
          this._mainContentEl.vaVideoEl.videoEl.videoHeight,
        originSetByUser: this._mainContentEl.vaVideoEl.originSetByUser,
        scaleSetByUser: this._mainContentEl.vaVideoEl.scaleSetByUser,
      });
    }
    this.dispatchEvent(
      new CustomEvent('open-dialog', {
        bubbles: true,
        composed: true,
        detail: {
          dialog: 'export',
          params: {
            onTabOptionsChange: async (tab, options) => {
              if (tab.type === 'frame') {
                tab.image = await this._exportVideo(options);
              }
            },
            tabs: exportTabs,
            notes: await dataWorld.getNotesText(),
          },
        },
      }),
    );
  }

  async onOpenFile(e) {
    const { fileData } = e.detail;
    const { experiment } = this.$services;

    try {
      const fileOpened = useServicesNext
        ? await experiment.iface.importData({ fileData })
        : await experiment.importData({ fileData });

      if (fileOpened) {
        this.experimentName = fileData.file.name;
        this.showWelcomeDialog = false;
        this.videoLoaded = true;
      }
    } catch (err) {
      console.error(err);
    }
  }

  async onCreateExperiment() {
    const { popoverManager } = this.$services;
    popoverManager.closeAll();

    try {
      const { cancelled } = await this.trySave().catch(rejection => rejection);
      if (cancelled) {
        this.showWelcomeDialog = false;
        return;
      }
      this.showWelcomeDialog = true;
    } catch (err) {
      console.error(err);
    }
  }

  stateChanged(state) {
    this.isSessionEmpty = state.isSessionEmpty;
    this.graphsState = state.graphs;
    if (this.inReplayMode !== state.links.replayMode) {
      this.inReplayMode = state.links.replayMode;

      if (!this.$services.dataWorld.sessionClosing) {
        if (this.inReplayMode) {
          this.$services.vaAppService.enableVideoDataReplay();
        } else this.$services.vaAppService.disableVideoDataReplay();
      }
    }
  }

  async _toggleReplay() {
    if (this.inReplayMode) {
      VaApp._disableReplay();
    } else {
      await this._enableReplay();
      this.#replayRef.value.focus();
      captureEvent('replay_enabled');
    }
  }

  async _enableReplay() {
    if (!this.replayControlsImported) {
      await import('@components/vst-ui-replay-controls/vst-ui-replay-controls.js');
      const vaVideo = this._mainContentEl.vaVideoEl;
      const falsifyPlaying = () => {
        this.#replayRef.value.playing = false;
      };
      this.addEventListener('video-ended', falsifyPlaying);
      this.addEventListener('video-pause', falsifyPlaying);
      this.addEventListener('replay-start', ({ detail }) => {
        const { replaySpeed } = detail;
        vaVideo.playbackRate = replaySpeed;
        requestAnimationFrame(() => {
          vaVideo._togglePlay();
        });
      });
      this.addEventListener('replay-pause', () => {
        vaVideo._togglePlay();
      });
      this.addEventListener('replay-start-over', () => {
        vaVideo.seekToFrame(0);
      });
      this.addEventListener('replay-rewind', ({ detail }) => {
        const { replaySpeed } = detail;
        vaVideo.seekToFrame(vaVideo.currentFrame - 1 * (replaySpeed || 1));
      });
      this.addEventListener('replay-advance', ({ detail }) => {
        const { replaySpeed } = detail;
        vaVideo.seekToFrame(vaVideo.currentFrame + 1 * (replaySpeed || 1));
      });
      this.replayControlsImported = true;
    }
    store.dispatch(enableReplay());
    store.dispatch(setReplayTimestamp(0));
  }

  async startCollection() {
    const { dataWorld } = this.$services;
    try {
      await dataWorld.startCollection();
    } catch (err) {
      console.error(err);
    }
  }

  async stopCollection() {
    const { dataWorld } = this.$services;
    try {
      await dataWorld.stopCollection();
    } catch (err) {
      console.error(err);
    }
  }

  async onImportVideo(e) {
    const { file } = e.detail;
    const { vaAppService, dataWorld } = this.$services;

    try {
      dataWorld.beginChurn('importVideo');
      await vaAppService.setVideo(file);

      this.showWelcomeDialog = false;
      this.videoLoaded = true;
    } catch (err) {
      console.error(err);
    }
  }

  _resizeViewport() {
    this.style.transform = '';
    const rect = this.getBoundingClientRect();
    if (rect.y !== 0) {
      window.scrollTo(0, 0);
      document.body.scrollTop = 0;
    }
  }

  _closeWelcome() {
    this.showWelcomeDialog = false;
    this.startAppTour();
  }

  render() {
    return html`
      </vst-ui-soft-alert>
      <vst-ui-toolbar
        class="toolbar"
        background="dark"
        notify="${this.notify}"
        collectButtonEnabled
        ?isCollecting="${this.isCollecting}"
        @start-collection="${this.startCollection}"
        @stop-collection="${this.stopCollection}"
      >
        <vst-ui-replay-controls
          ${ref(this.#replayRef)}
          background="dark"
          class="replay-controls"
          maxReplaySpeed="10"
          ?small="${isSmallScreen()}"
          ?hidden="${!this.inReplayMode}"
          ?connections-block="${CONNECTIONS_APP_CONTEXT}"
          @closed="${VaApp._disableReplay}"
          slot="toolbar_replay"
        ></vst-ui-replay-controls>
        <vst-ui-dropdown
          ?open="${this.showFileMenu}"
          background="dark"
          slot="toolbar_left"
          @closed="${() => this._toggleFileMenu(!this.showFileMenu)}"
          position="bottom-left"
          variant="dropdown"
        >
          <vst-style-tooltip slot="anchor">
            <button
              aria-expanded="${this.showFileMenu}"
              aria-label=${sprintf(
                getText(
                  'File Menu for %s',
                  'general',
                  '%s is the file name for the current experiment',
                ),
                this.experimentName,
              )}
              class="btn"
              id="new_btn"
              variant="toolbar"
              @click="${() => this._toggleFileMenu(!this.showFileMenu)}"
            >
              <vst-ui-icon .icon="${file}"></vst-ui-icon>
              <div class="toolbar__filename">
                ${this.experimentName}
              </div>
            </button>
            <span role="tooltip" position="block-end-start">${getText('File Menu')}</span>
          </vst-style-tooltip>
          <vst-ui-listbox
            slot="content"
            .view="${this.fileMenuItems}"
            @list-item-clicked="${this._handleFileMenuItemClicked}"
          ></vst-ui-listbox>
        </vst-ui-dropdown>
                ${when(
                  isFeatureFlagEnabled('ff-app-tour'),
                  () => html`
                    <vst-style-tooltip slot="toolbar_right">
                      <button
                        ${ref(this._tourButtonRef)}
                        aria-labelledby="tour-btn-tooltip"
                        class="btn"
                        variant="toolbar"
                        id="tour-btn"
                        @click="${() => {
                          this.startAppTour(true);
                        }}"
                      >
                        ${helpIcon}
                      </button>
                      <span id="tour-btn-tooltip" role="tooltip" position="block-end"
                        >${getText('App Tour')}</span
                      >
                    </vst-style-tooltip>
                  `,
                )}
        ${conditionalTemplate(
          this.videoLoaded,
          html`
            <vst-style-tooltip slot="toolbar_right">
              <button
                aria-expanded="${this.inReplayMode}"
                aria-labelledby="toggle-replay-btn-text"
                id="toggle_replay_btn"
                class="btn"
                variant="toolbar"
                ?active="${this.inReplayMode}"
                @click="${this._toggleReplay}"
              >
                <vst-ui-icon .icon="${videoAdvancePrev}"></vst-ui-icon>
              </button>
              <span id="toggle-replay-btn-text" role="tooltip" position="block-end"
                >${getText('Enable Replay')}</span
              >
            </vst-style-tooltip>
          `,
        )}
        <vst-ui-dropdown
          position="bottom-right"
          variant="dropdown"
          slot="toolbar_right"
          background="dark"
          ?open="${this.showViewChangeMenu}"
          @closed="${this._toggleViewChangeMenu}"
        >
          <vst-style-tooltip slot="anchor">
            <button
              aria-expanded="${this.showViewChangeMenu}"
              aria-labelledby="view-change-btn-text"
              class="btn"
              id="view_change_btn"
              variant="toolbar"
              @click="${this._toggleViewChangeMenu}"
            >
              <vst-ui-icon .icon="${view}"></vst-ui-icon>
            </button>
            <span id="view-change-btn-text" role="tooltip" position="block-end-end">${getText('View Options')}</span>
          </vst-style-tooltip>
          <vst-ui-content-layout-options
            slot="content"
            .layoutOptions="${[
              {
                title: getText('Video'),
                id: 'video',
                checked: vstLayoutStore.video,
              },
              {
                title: getText('Graph'),
                id: 'graphs',
                checked: vstLayoutStore.graph_1,
              },
              {
                title: getText('Data Table'),
                id: 'table',
                checked: vstLayoutStore.table,
              },
              {
                title: getText('Notes'),
                id: 'notes',
                checked: vstLayoutStore.notes,
              },
            ]}"
            .graphOptions="${[
              {
                title: getText('1 Graph'),
                id: 'graph_1',
                selected: vstLayoutStore.graph_1,
              },
              {
                title: getText('2 Graphs'),
                id: 'graph_2',
                selected: vstLayoutStore.graph_2,
              },
            ]}"
            @layout-option-changed="${VaApp._handleViewMenuItemChanged}"
          ></vst-ui-content-layout-options>
        </vst-ui-dropdown>

        <vst-style-tooltip slot="toolbar_right">
          <button
            aria-expanded="${this._moreOptionsMenuOpen}"
            aria-labelledby="overflow-btn-text"
            class="btn toolbar-overflow ${this.notify ? `toolbar-overflow--${this.notify}` : ''}"
            id="overflow_btn"
            variant="toolbar"
            @click="${this._toggleOverflowMenu}"
          >
            <vst-ui-icon .icon="${overflow}"></vst-ui-icon>
            ${conditionalTemplate(
              this.notify,
              html`<span class="toolbar-overflow--${this.notify}__dot"></span>`,
            )}
          </button>
          <span id="overflow-btn-text" role="tooltip" position="block-end-end">${getText('Other Options')}</span>
        </vst-style-tooltip>
        <vst-ui-popover
          for="overflow_btn"
          id="overflow_popover"
          placement="bottom"
          slot="toolbar_right"
          ?open="${this._moreOptionsMenuOpen}"
          @closed=${() => {
            this._moreOptionsMenuOpen = false;
          }}
        >
          <vst-ui-listbox
            id="overflow-list"
            .view="${this.overflowMenuItems}"
            @list-item-clicked="${this._handleOverflowItemClicked}"
          ></vst-ui-listbox>
        </vst-ui-popover>
      </vst-ui-toolbar>

      <va-main-content
        id="main_content"
        class="main-content"
        video-loaded="${this.videoLoaded}"
        @import-video="${this.onImportVideo}"
      >
      </va-main-content>
      ${conditionalTemplate(
        this.license,
        html`
          <vst-ui-soft-alert
            aria-label="${getText('Pro Expiry Alert')}"
            id="soft_alert_bottom"
            class="soft_alert_trial"
            ?hidden=${this.daysRemaining > DAYS_TO_SHOW_WARNING}
            type=${this.daysRemaining < DAYS_TO_SHOW_ERROR ? 'error' : 'warning'}
            canClose
          >
            ${when(
              this.daysRemaining > 1,
              () =>
                getText(`Vernier Video Analysis® Pro access expires in ${this.daysRemaining} days`),
              () =>
                when(
                  this.licenseExpired,
                  () =>
                    sprintf(
                      getText(`Vernier Video Analysis® Pro access expired %s`),
                      this.licenseExpiration,
                    ),
                  () => getText(`Vernier Video Analysis® Pro access expires today`),
                ),
            )}
          </vst-ui-soft-alert>
        `,
      )}
      <div class="popover-wrapper" id="popover_wrapper"></div>
      <div id="toast_wrapper"></div>
      <slot name="tooltip_wrapper"></slot>
      <vst-ui-dialog-manager>
        ${this.dialogs.templates.map(template => template.call(this))}
      </vst-ui-dialog-manager>
      ${conditionalTemplate(
        this.$services,
        html`
          <vst-ui-dialog
            @dialog-close="${this._closeWelcome}"
            id="welcome_dialog"
            ?open="${this.showWelcomeDialog}"
          >
            ${conditionalTemplate(
              this.showWelcomeDialog,
              html`
                <div slot="version" class="caption" size="s" id="header-version">
                  v${this.$services?.appManifest.getAppVersion()}
                </div>
                <svg
                  slot="header"
                  viewBox="0 0 230 16"
                  class="header__logo"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <title>${getText('Vernier Video Analysis®')}</title>
                  <path
                    d="M7 15.8H4.7L0 3.2h2l3.9 11 4-11h1.9L7 15.8zm6.6-12.6h7v1.6h-5.2v3.7h4.8v1.6h-4.8v4.1h5.2v1.6h-7V3.2zm9.8 0h3.1c2.5 0 4.9.4 4.9 3.4 0 1.5-1.1 2.6-2.6 2.9.5.2.8.5 1.2 1.3l2.4 5h-2.1l-1.9-4.4c-.6-1.2-1.2-1.2-2.1-1.2h-1.1v5.6h-1.8V3.2zm1.8 5.4h1.5c1.7 0 2.7-.8 2.7-2 0-1.1-.9-1.9-2.5-1.9h-1.7v3.9zm9.3-5.4h2.4l5.5 10.1V3.2h1.8v12.6H42L36.3 5.4v10.3h-1.8V3.2zm13 0h1.8v12.6h-1.8V3.2zm5.1 0h7v1.6h-5.2v3.7h4.8v1.6h-4.8v4.1h5.2v1.6h-7V3.2zm9.8 0h3.1c2.5 0 4.9.4 4.9 3.4 0 1.5-1.1 2.6-2.6 2.9.5.2.8.5 1.2 1.3l2.4 5h-2.1l-1.9-4.4c-.6-1.2-1.2-1.2-2.1-1.2h-1.1v5.6h-1.8V3.2zm1.8 5.4h1.5c1.7 0 2.7-.8 2.7-2 0-1.1-.9-1.9-2.5-1.9h-1.7v3.9zm26.5-5.4l-4.8 12.6h-2.2L78.9 3.2h2l3.9 11 4-11h1.9zm1.7 12.6V3.2h1.8v12.6h-1.8zm4.9 0V3.2h3.7c1.9 0 3.5.6 4.8 1.7 1.3 1.1 1.9 2.6 1.9 4.5 0 2.1-.6 3.7-1.8 4.8-1.2 1.1-2.8 1.6-4.9 1.6h-3.7zm1.8-11v9.4h1.9c3.2 0 4.8-1.6 4.8-4.7 0-1.4-.4-2.6-1.3-3.4-.9-.8-2-1.3-3.3-1.3h-2.1zm11.4 11V3.2h7v1.6h-5.2v3.7h4.8v1.6h-4.8v4.1h5.2v1.6h-7zm9.3-6.3c0-1.9.5-3.5 1.6-4.7 1.1-1.2 2.6-1.8 4.5-1.8s3.3.6 4.4 1.8c1.1 1.2 1.6 2.8 1.6 4.7 0 1.9-.5 3.5-1.6 4.7-1.1 1.2-2.6 1.8-4.4 1.8-1.8 0-3.4-.6-4.4-1.8-1.2-1.2-1.7-2.8-1.7-4.7zm1.9 0c0 1.4.4 2.5 1.1 3.5.7 1 1.7 1.4 3.1 1.4 1.4 0 2.4-.5 3.1-1.4.7-1 1.1-2.1 1.1-3.5 0-.9-.1-1.7-.4-2.4-.3-.7-.8-1.3-1.4-1.8-.6-.5-1.4-.7-2.3-.7-.9 0-1.7.2-2.3.7-.6.5-1.1 1.1-1.4 1.8-.3.7-.6 1.5-.6 2.4zm21.8-6.3h1.9l5.3 12.6h-2l-1.3-3.2h-6.2l-1.3 3.2H138l5.5-12.6zm.8 1.7l-2.5 6.2h5l-2.5-6.2zm8.2-1.7h2.4l5.5 10.1V3.2h1.8v12.6H160l-5.7-10.3v10.3h-1.8V3.2zm16.9 0h1.9l5.3 12.6h-2l-1.3-3.2h-6.2l-1.3 3.2h-1.9l5.5-12.6zm.9 1.7l-2.5 6.2h5l-2.5-6.2zm8.1-1.7h1.8v11h5.3v1.6h-7.1V3.2zm10.9 7.3l-4.9-7.3h2.1l3.7 5.6 3.7-5.6h2.1l-4.9 7.3v5.3h-1.8v-5.3zm14.2-5.4c-.9-.4-1.4-.5-2.4-.5s-2.2.4-2.2 1.7c0 2.4 5.4 1.9 5.4 6 0 2.7-2.1 3.7-4.6 3.7-.9 0-1.7-.3-2.6-.5l.2-1.7c.6.3 1.7.6 2.6.6 1 0 2.5-.6 2.5-2.1 0-2.8-5.4-2-5.4-5.9 0-2.2 1.8-3.4 4-3.4.8 0 1.8.1 2.7.5l-.2 1.6zm3.3-1.9h1.8v12.6h-1.8V3.2zm10.7 1.9c-.9-.4-1.4-.5-2.4-.5s-2.2.4-2.2 1.7c0 2.4 5.4 1.9 5.4 6 0 2.7-2.1 3.7-4.6 3.7-.9 0-1.7-.3-2.6-.5l.2-1.7c.6.3 1.7.6 2.6.6 1 0 2.5-.6 2.5-2.1 0-2.8-5.4-2-5.4-5.9 0-2.2 1.8-3.4 4-3.4.8 0 1.8.1 2.7.5l-.2 1.6zM220.811 4.729c0-.79.181-1.522.544-2.198a4.12 4.12 0 011.518-1.594 3.994 3.994 0 012.098-.585c.75 0 1.447.195 2.091.586a4.062 4.062 0 011.512 1.593c.367.676.551 1.408.551 2.198 0 .769-.176 1.49-.527 2.162a4.196 4.196 0 01-1.5 1.623 3.912 3.912 0 01-2.127.609c-.77 0-1.479-.201-2.127-.603a4.198 4.198 0 01-1.5-1.618 4.572 4.572 0 01-.533-2.173zm.673 0c0 .671.157 1.294.469 1.869.313.57.736 1.017 1.272 1.341a3.328 3.328 0 001.746.487c.629 0 1.213-.164 1.752-.492a3.495 3.495 0 001.265-1.348 3.815 3.815 0 00.463-1.857 3.77 3.77 0 00-.463-1.846 3.41 3.41 0 00-1.265-1.336 3.308 3.308 0 00-1.752-.492c-.625 0-1.207.162-1.746.486-.539.32-.965.766-1.278 1.336a3.836 3.836 0 00-.463 1.852zm2.625.439v1.98h-.826V2.174h1.617c.598 0 1.061.129 1.389.387.332.253.498.625.498 1.113 0 .48-.25.847-.75 1.101.477.196.717.594.721 1.196v.357c0 .336.033.578.099.727v.093h-.849c-.055-.14-.082-.39-.082-.75 0-.363-.006-.578-.018-.644-.062-.375-.318-.57-.767-.586h-1.032zm0-.75h.914c.282-.008.508-.076.68-.205a.607.607 0 00.258-.51c0-.285-.076-.486-.229-.603-.152-.121-.423-.182-.814-.182h-.809v1.5z"
                    fill="#fff"
                  />
                </svg>
                <va-welcome
                  slot="content"
                  @import-video="${this.onImportVideo}"
                  @open-file-sequence="${this.#handleWelcomeOpenFile}"
                ></va-welcome>
                <div slot="footer" class="footer">
                  ${when(
                    this.license,
                    () => html`
                      <section
                        class="inline banner ${classMap({ expired: this.licenseExpired })}"
                        gap="m"
                        id="welcome-license-info"
                      >
                        <div>
                          <div class="inline" style="--align: baseline" gap="xs">
                            <div>
                              <p class="heading" size="s">${getText('Licensed to')}:</p>
                              <p class="caption">${this.licenseName}</p>
                            </div>
                          </div>
                          <div class="inline" style="--align: baseline" gap="xs">
                            <div>
                              <p class="heading" size="s">
                                ${this.licenseExpired
                                  ? getText('Subscription Ended')
                                  : getText('Subscription Ends')}:
                              </p>
                              <p class="caption">${this.licenseExpiration}</p>
                            </div>
                          </div>
                        </div>
                      </section>
                    `,
                  )}
                </div>
              `,
            )}
          </vst-ui-dialog>
        `,
      )}

      <vst-ui-dialog
        ?open="${this._showAboutDialog}"
        id="va-about"
        @dialog-close="${() => {
          this._showAboutDialog = false;
        }}"
      >
        <h3 slot="header">${getText('About')}</h3>
        <vst-ui-about
          slot="content"
          @logo-secret-clicked="${this.#onOpenFeatureFlagsDialog}"
        >
          <va-logo slot="logo" class="app-logo"></va-logo>
          <span slot="version">${this.$services?.appManifest.getAppVersion()}</span>
          <span slot="copyright">
            ${sprintf(getText(`© 2020-%s Vernier Science Education`), CURRENT_YEAR)}
          </span>
          <vst-ui-license-info
            slot="license"
            .licenseExpiration="${this.licenseExpiration}"
            .licenseExpired="${this.licenseExpired}"
            .licenseName="${this.licenseName}"
          ></vst-ui-license-info>
          <p slot="about-text">
            ${getText(
              'Vernier Video Analysis® is a tool for science students to analyze the motion of real-world objects in videos.',
            )}
          </p>
          <p class="caption" id="ffmpeg_terms" size="s" slot="terms-extra" >
            ${getText('This software uses libraries from the FFmpeg project under the LGPLv2.1')}
          </p>
        </vst-ui-about>
      </vst-ui-dialog>

      <vst-ui-dialog
        id="feature-flags"
        stop-propagation
        ?open="${this._showFeatureFlagsDialog}"
        @dialog-close="${() => {
          this._showFeatureFlagsDialog = false;
        }}"
      >
        <h2 slot="header">${getText('Experimental Features')}</h2>
        <vst-core-feature-flags slot="content"></vst-core-feature-flags>
      </vst-ui-dialog>

      <vst-ui-dialog
        @dialog-close="${() => {
          this.showSessionPreferencesDialog = false;
        }}"
        id="session-preferences"
        ?open="${this.showSessionPreferencesDialog}"
      >
        <h2 slot="header">${getText('Session Preferences')}</h2>
        <vst-core-session-preferences slot="content"></vst-core-session-preferences>
      </vst-ui-dialog>

      <vst-ui-dialog
        @dialog-close="${() => {
          this._toggleAppPreferencesDialog(false);
        }}"
        id="app-preferences"
        ?open=${this._showAppPreferences}
      >
        <h2 slot="header" margin="0">${getText('App Preferences')}</h2>
        <vst-ui-app-preferences slot="content"></vst-ui-app-preferences>
      </vst-ui-dialog>

      ${this.cookieConsentDialogTemplate()}
    `;
  }
}

customElements.define('va-app', VaApp);
